import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { Loader2, Building2, CreditCard, Copy, Check, Clock, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DELIVERY_REGIONS = [
  { value: 'Abuja', label: 'Abuja', fee: 2000 },
  { value: 'Lagos', label: 'Lagos', fee: 3000 },
  { value: 'Port Harcourt', label: 'Port Harcourt', fee: 3500 },
  { value: 'Kano', label: 'Kano', fee: 3500 }
];

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, getCartTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('paystack');
  const [copiedAccount, setCopiedAccount] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    region: ''
  });

  useEffect(() => {
    fetchBankAccounts();
  }, []);

  const fetchBankAccounts = async () => {
    try {
      const response = await axios.get(`${API}/bank-accounts/active`);
      setBankAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
    }
  };

  const copyToClipboard = (text, accountId) => {
    navigator.clipboard.writeText(text);
    setCopiedAccount(accountId);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedAccount(null), 2000);
  };

  const selectedRegion = DELIVERY_REGIONS.find(r => r.value === formData.region);
  const deliveryFee = selectedRegion ? selectedRegion.fee : 0;
  const total = getCartTotal() + deliveryFee;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleRegionChange = (value) => {
    setFormData({
      ...formData,
      region: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    if (!formData.region) {
      toast.error('Please select a delivery region');
      return;
    }

    setLoading(true);

    try {
      // Create order
      const orderData = {
        customer: {
          email: formData.email,
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone: formData.phone,
          address: {
            street: formData.street,
            city: formData.city,
            state: formData.state,
            region: formData.region,
            phone: formData.phone
          }
        },
        items: cart.map(item => ({
          product_id: item.id,
          product_name: item.name,
          quantity: item.quantity,
          unit_price: item.price
        }))
      };

      const orderResponse = await axios.post(`${API}/orders/create`, orderData);
      const { order_id, paystack_reference, total_amount } = orderResponse.data;

      // Initialize payment
      const paymentData = {
        order_id: paystack_reference,
        email: formData.email,
        amount: total_amount
      };

      const paymentResponse = await axios.post(`${API}/payment/initialize`, paymentData);
      
      if (paymentResponse.data.success) {
        // Store order info for confirmation page
        localStorage.setItem('pending_order', JSON.stringify({
          order_id,
          order_number: orderResponse.data.order_number,
          reference: paystack_reference
        }));

        // Redirect to Paystack or verify immediately (test mode)
        if (paymentResponse.data.authorization_url && paymentResponse.data.authorization_url !== 'https://checkout.paystack.com/test') {
          window.location.href = paymentResponse.data.authorization_url;
        } else {
          // Test mode - verify immediately
          await axios.post(`${API}/payment/verify/${paystack_reference}`);
          clearCart();
          navigate(`/order-confirmation/${order_id}`);
        }
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process order');
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="checkout-page py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Checkout</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-6">Delivery Information</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        required
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        required
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder="Doe"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+234 XXX XXX XXXX"
                    />
                  </div>

                  <div>
                    <Label htmlFor="street">Street Address *</Label>
                    <Input
                      id="street"
                      name="street"
                      required
                      value={formData.street}
                      onChange={handleChange}
                      placeholder="123 Main Street"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        name="city"
                        required
                        value={formData.city}
                        onChange={handleChange}
                        placeholder="Lagos"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State *</Label>
                      <Input
                        id="state"
                        name="state"
                        required
                        value={formData.state}
                        onChange={handleChange}
                        placeholder="Lagos State"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="region">Delivery Region *</Label>
                    <Select value={formData.region} onValueChange={handleRegionChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select delivery region" />
                      </SelectTrigger>
                      <SelectContent>
                        {DELIVERY_REGIONS.map((region) => (
                          <SelectItem key={region.value} value={region.value}>
                            {region.label} - ₦{region.fee.toLocaleString()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Payment Method Selection */}
                  <div className="border-t pt-6">
                    <Label className="text-lg font-semibold mb-4 block">Payment Method</Label>
                    <div className="space-y-3">
                      <label className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        paymentMethod === 'paystack' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                      }`}>
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="paystack"
                          checked={paymentMethod === 'paystack'}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          className="mr-3"
                        />
                        <CreditCard className="h-5 w-5 mr-3 text-green-600" />
                        <div>
                          <span className="font-semibold">Pay with Card (Paystack)</span>
                          <p className="text-sm text-gray-500">Secure card payment via Paystack</p>
                        </div>
                      </label>

                      {bankAccounts.length > 0 && (
                        <label className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          paymentMethod === 'bank_transfer' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                        }`}>
                          <input
                            type="radio"
                            name="paymentMethod"
                            value="bank_transfer"
                            checked={paymentMethod === 'bank_transfer'}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                            className="mr-3"
                          />
                          <Building2 className="h-5 w-5 mr-3 text-blue-600" />
                          <div>
                            <span className="font-semibold">Bank Transfer</span>
                            <p className="text-sm text-gray-500">Transfer to our bank account</p>
                          </div>
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Bank Account Details (shown when bank transfer is selected) */}
                  {paymentMethod === 'bank_transfer' && bankAccounts.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                      <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                        <Building2 className="h-5 w-5" />
                        Bank Account Details
                      </h3>
                      <p className="text-sm text-blue-800">
                        Transfer <strong>₦{total.toLocaleString()}</strong> to any of the accounts below:
                      </p>
                      
                      <div className="space-y-3">
                        {bankAccounts.map((account) => (
                          <div key={account._id} className="bg-white p-4 rounded-lg border">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-bold text-gray-900">{account.bank_name}</p>
                                <p className="text-lg font-mono text-gray-800">{account.account_number}</p>
                                <p className="text-sm text-gray-600">{account.account_name}</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => copyToClipboard(account.account_number, account._id)}
                                className="p-2 bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors"
                                title="Copy account number"
                              >
                                {copiedAccount === account._id ? (
                                  <Check className="h-4 w-4" />
                                ) : (
                                  <Copy className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <p className="text-xs text-blue-700 mt-2">
                        After payment, please send proof of payment to <strong>sale@dayefarmltd.com</strong> or via WhatsApp
                      </p>
                      
                      {/* 48-hour Payment Notice */}
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                        <div className="flex items-start gap-2">
                          <Clock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div className="text-sm">
                            <p className="font-semibold text-amber-800">48-Hour Payment Window</p>
                            <p className="text-amber-700">
                              Please complete your bank transfer within 48 hours to secure your order. 
                              Unpaid orders will be automatically cancelled and stock will be released.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Processing...
                      </>
                    ) : paymentMethod === 'bank_transfer' ? (
                      `Place Order - ₦${total.toLocaleString()}`
                    ) : (
                      `Pay ₦${total.toLocaleString()}`
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24 border-2 border-green-100">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Order Summary</h2>

                <div className="space-y-4 mb-6">
                  {cart.map((item) => (
                    <div key={item.id} className="flex justify-between text-sm">
                      <span className="text-gray-700">
                        {item.name} x {item.quantity}
                      </span>
                      <span className="font-semibold">
                        ₦{(item.price * item.quantity).toLocaleString()}
                      </span>
                    </div>
                  ))}
                  
                  <div className="border-t pt-4 space-y-2">
                    <div className="flex justify-between text-gray-700">
                      <span>Subtotal</span>
                      <span className="font-semibold">₦{getCartTotal().toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-gray-700">
                      <span>Delivery Fee</span>
                      <span className="font-semibold">
                        {deliveryFee > 0 ? `₦${deliveryFee.toLocaleString()}` : 'Select region'}
                      </span>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex justify-between text-xl font-bold">
                      <span>Total</span>
                      <span className="text-green-600">₦{total.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-green-800">
                    <strong>Secure Payment:</strong> {paymentMethod === 'paystack' 
                      ? 'Your payment is processed securely through Paystack.'
                      : 'Transfer to our verified bank account. Order confirmed after payment verification.'}
                  </p>
                </div>
                
                {/* 48-hour Order Reservation Notice */}
                <div className="bg-gray-50 p-4 rounded-lg mt-4 border border-gray-200">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-gray-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-gray-600">
                      <strong>Important:</strong> Your items are reserved for 48 hours after placing an order. 
                      Orders not paid within this period will be automatically cancelled.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;